// 168B §1b — THE ESLINT RATCHET'S OWN REMEDY, the same one §1a applied to
// EntityList.tsx: 75 of this file's warnings are `react/react-in-jsx-scope`,
// one per JSX element, and the baseline leaves two of headroom. The JSX
// transform stays automatic ("jsx": "react-jsx"), so nothing emitted moves.
import React from "react";
import type { Dispatch } from "react"; // 189 §H
import MoneyStack from "../components/MoneyStack"; // 169 R7
import {
  pname,
  faddr,
  fq,
  fc,
  fd,
  SK,
  getInternalRate,
  canEditInternalRates,
  weeklyHours,
  normalizeWeeklySchedule,
} from "../utils";
import { roleDefaultRatio } from "../hooks/useRoleLabels";
import { resolveTaxonomyLabel } from "./taxonomyHelpers";
import { clientNameCell } from "./clientNameCell"; // 168 — the person's prospect tag
// 168B §1c — the client record's own standing cell, mounted here rather than
// redrawn (§1c: "REUSE that cell, do not draw a third").
import { clientArchivedChip, clientStandingPill, clientStatusShown } from "./clientStandingPill";
import type { ClientStanding } from "./clientStandingPill";
import type { TFn } from "./linkStatus";
// 168B §1c — the link vocabulary IN LIFECYCLE ORDER, which is what `sv` ranks
// by. The same array `linkStatusOptions` maps for the roster's picker.
import { KNOWN_STATUSES } from "./collectiveSegments.mjs";
// 59 §3 — a COLUMN render receives the item and nothing else (EntityList calls
// `c.r(it)`), so the badge below has no `t` in scope. The imperative resolver
// reads the row at call time and returns the fallback when nothing is loaded,
// which is what keeps the byte-identity freeze green.
import { tImperative as _tImp } from "../i18n";
import { storedClientLanguage } from "./docLang.mjs"; // 207C §6 — a client's language as stored

// 59f — the opponent type's two codes, resolved the way its editor writes them
// (`App.tsx:7176` / `:7770` label the same pair `type.company` /
// `type.individual`). NOT a taxonomy: `census-rendered-codes.js` reports this
// column ORPHAN because no kind contains these values. An unrecognised code —
// `natural`, on 24 of the 26 opponents on staging — falls through to itself,
// which is 05m's orphan-visible posture and the reason this is not a `??`.
const opponentTypeLabel = (code?: string) =>
  code === "company" || code === "individual"
    ? _tImp(`type.${code}`, { fallback: code === "company" ? "Company" : "Individual" })
    : code;
import { Copy } from "lucide-react";
import { CT, RAD, FS } from "./tokens";
import ScheduleGrid from "../views/ScheduleGrid";
import ClientCasesCard from "../views/ClientCasesCard";
import PartyMattersCard from "../views/PartyMattersCard"; // 214 §6 — a lawyer's record lists his matters
import { isClientCounsel, roleCodeOf, type LpFn, type NavFn } from "./counselLinks"; // 214 §4 — the role Avocat
import CddClientSection from "../views/CddClientSection";
// 182 A.3 — the Notes section on the client record (the contact timeline).
import InteractionSection from "../components/InteractionSection";
import {
  clientMatterSummary,
  clientRegularCases,
  clientCollectiveCases,
  matterAmountDue, matterAmountDueForeign, mergeForeign,
} from "./clientMatters.mjs";
import Table from "../components/Table";
import type { CustomFieldColumn } from "./customFieldColumns"; // 189 §B
import { numbersByClient, numbersShown } from "./memberRefs.mjs"; // 211 §19 — a client found by its numbers in any collective case
/* 207C §6 — a stored language code → the client form's own word for it (`lang.*`, seeded ×3). */
const LANGUAGE_WORDS: Readonly<Record<string, readonly [string, string]>> = {
  fr: ["lang.french", "French"],
  nl: ["lang.dutch", "Dutch"],
  en: ["lang.english", "English"],
  de: ["lang.german", "German"],
};

/* 168B §1b — WHAT `cl`'s `rowCtx` ANSWERS WITH. `clientMatters.mjs` is
   JavaScript, so its return is untyped at this boundary; naming the shape here
   is what lets the four computed columns read it without an `any`
   (`scripts/lint/ratchets.js` holds this file at 131 and it is at 130). */
type ClientSummary = {
  hasCaseLink: boolean;
  mostRecentStatus: string | null;
  mostRecentStatusDate: Date | null;
  mostRecentStanding: ClientStanding | null;
  mostRecentMatter: { isProspect?: boolean } | null;
  totalDue: number;
  /** 169 R7 — the other currencies' dues, per code; null when none. */
  totalDueForeign?: Record<string, number> | null;
  hasDue: boolean;
  titles: string[];
  /** 169 R7 — the row context's gated foreign dues (ecfg rowCtx), per code. */
  dueForeign?: Record<string, number> | null;
  matterStatuses: string[];
  /* 168B §1e — the AUTHORIZED half of `totalDue`: the sum over the matters
     this viewer may see figures for, and whether that was any of them.
     Computed in `rowCtx` (once per row per render) because `sv` would
     otherwise re-walk every case and every invoice on each of the O(n log n)
     comparisons a sort makes. */
  dueSeen: boolean;
  dueSum: number;
};

/* 168B — the slice of EntityList's capability context these columns read. It is
   the SAME object every capability on this entity is handed; naming the two
   fields used rather than typing it `any` is what keeps
   `scripts/lint/ratchets.js` green (it holds this file at 131 `any` nodes and
   the file sits at 130). */
type ListCtx = {
  t: TFn;
  caseStatusesList?: [string, string][];
  canSeeAnyDue?: boolean;
  /* 168B §1f — the two app services the contact cells reach for. They are
     ctx entries and not direct imports for one measured reason: `openCompose`
     ends in `window.open` and a clipboard write ends in `navigator`, and
     `recordings/57c-entitylist-freeze` DRIVES every host `onClick` it finds in
     the tree. A named seam is what that harness freezes; a global is what it
     cannot see and cannot stub. */
  composeTo?: (to: string) => void;
  copyText?: (text: string) => void;
  /* 189 §H — opens the one-to-one template modal on a client (App's state). */
  openMailTemplate?: Dispatch<MailTemplateClient>;
};
/** The client fields the one-to-one template modal reads. */
interface MailTemplateClient { id: number; name?: string | null; firstName?: string | null; lastName?: string | null; email?: string | null }

/** The two fields the contact cells read. */
type ClientContactRow = { email?: string | null; phone?: string | null };

/** The person's own prospect tag, which since the 168B glance ruling is what
 *  the Client-status column answers with when it is set. */
type ClientTagRow = { isProspect?: boolean | null };

/* 168B §1f — the copy affordance, on both contact cells. MEASURED: no shared
   copy control exists in this tree — `LibraryView` copies a reference from a
   cell click, `Phase4SettingsPanels` and `CaseDocumentsSection` each have their
   own button — so this is the brief's fallback, "a small copy icon on hover
   with a toast". `common.copy` and `toast.clipboard_denied` are already seeded
   in three languages, so it adds no string. `.copy-aff` (App.css) is what makes
   it appear on the row's hover and on keyboard focus. */
const copyAff = (ctx: ListCtx, value: string) => (
  <button
    type="button"
    className="copy-aff"
    title={ctx.t("common.copy", { fallback: "Copy" })}
    onClick={(e) => {
      e.stopPropagation();
      if (ctx.copyText) ctx.copyText(value);
    }}
  >
    <Copy size={11} />
  </button>
);

/** A `case_status` code resolved through the taxonomy list the app loads,
 *  falling back to the code itself so an orphan stays visible (05m's posture).
 *  Module scope because two surfaces ask it now: the Client-status column's
 *  inherited arm and the filter-driven `_xstatus` column. */
const caseStatusLabel = (
  list: [string, string][] | undefined,
  code: string | null | undefined,
): string => {
  const e = (list || []).find(([v]) => v === code);
  return e ? e[1] : code || "—";
};

// Brief 57 §3a — the entity facts that lived as `et === "..."` branches inside
// EntityList.tsx are DECLARED here, on the entity they are about. Each
// capability receives `(it, ctx)` — ctx being the union of what EntityList
// holds (src/components/EntityList.tsx names the fields) — so this builder's
// own inputs are unchanged. The bodies are MOVED, not improved: brief-57's
// freeze (recordings/57c-entitylist-freeze) replays all thirteen screens
// byte-identical against them.

// The English entity nouns interpolated into the empty-state fallbacks
// (`No ${lbl}s yet`). A NAMED map on purpose: prose-map (i18n-total) watches
// named lookup tables, and the per-contract exemption for these eleven nouns
// follows this map — inlining them as bare per-entry literals would narrow
// the instrument's reach from eleven watched words to one, silently.
const ENTITY_NOUNS = {
  cl: "client",
  op: "opponent",
  ocu: "opponent counsel",
  ex: "expert",
  ju: "jurisdiction",
  co: "collaborator",
  cp: "contact person",
  oc: "opponent contact",
  me: "mediator",
  ar: "arbiter",
  op2: "person",
};

// The collaborator facts, shared by the three collaborator entries (co2int /
// co2ext / co2 — one entity, three item filters).
/* 75 §3/§4 — the management lists are DISPLAY, so archived people stay listed
   (they must, or nobody could restore them) and read as archived: name greyed,
   a state tag beside it. Suspended people keep a normal name and gain the tag.
   One cell renderer for all three collaborator lists. */
const coNameCell = (i: any) => {
  // An active collaborator renders EXACTLY as before — byte-identical, so the
  // 57c entitylist freeze (all-active fixture) does not move. Only a suspended
  // or archived person gets the greyed name + state tag.
  if (!i.status || i.status === "active") {
    return <span className="name-cell">{pname(i)}</span>;
  }
  return (
    <span
      className="name-cell"
      style={i.status === "archived" ? { opacity: 0.45 } : undefined}
    >
      {pname(i)}
      <span className="tag" style={{ marginLeft: 6 }}>
        {i.status === "suspended"
          ? _tImp("co.status.suspended", { fallback: "Suspended" })
          : _tImp("co.status.archived", { fallback: "Archived" })}
      </span>
    </span>
  );
};

const CO_FACTS = {
  label: ENTITY_NOUNS.co,
  /* Round 13 (C74c) — collaborator records: editing ANY other collaborator is
     admin-only entirely; a non-admin may open the edit modal for HIS OWN row
     only (the modal's field matrix then locks everything but initials +
     phone). Delete stays admin-only (server gate). */
  canEditRow: (it: any, { _appIsAdmin, currentUser }: any) => !!_appIsAdmin || it.id === currentUser,
  canDeleteRow: ({ _appIsAdmin }: any) => !!_appIsAdmin,
  /* 05k §2 — DELETING A COLLABORATOR IS NOT WHAT DELETING IS ELSEWHERE.
     `common.delete_confirm` ("Delete?") serves the other entities; a
     collaborator's delete is refuse-gated server-side and stays for the
     mistake-row case only (ruled 2026-08-02: real people are deactivated,
     never deleted). 75 §3 — the Archive alternative EXISTS now (05j), so the
     sentence names it instead of denying it. The wording follows the
     pro-forma sentence shape; the absence of a shared destructive-confirm
     primitive is registered (05h), not invented in passing. */
  deleteConfirm: {
    bannerStyle: { flexWrap: "wrap" } as any,
    spanStyle: { flex: "1 1 100%", marginBottom: 4 } as any,
    text: ({ t }: any) =>
      t("collaborator.delete_confirm", {
        fallback:
          "Delete this collaborator permanently? A deleted collaborator cannot be restored. Only a collaborator who has touched nothing can be deleted — a real person is archived instead, from their edit form.",
      }),
    confirmLabel: ({ t }: any) =>
      t("common.delete_permanently", {
        fallback: "Delete permanently",
      }),
  },
  onDelete: (it: any, { collaborators, del, setCollaborators }: any) =>
    del(SK.co, collaborators, setCollaborators, it.id),
  weeklyScheduleCard: (it: any, { _appIsAdmin, _appPerms, collaborators, currentUser, persist, schedAxis, setCollaborators, t }: any) =>
    (_appIsAdmin || _appPerms.can("caseView.terms.view")) &&
    (() => {
      const canEditSched = currentUser === it.id || _appIsAdmin;
      const others = collaborators
        .filter(
          (c: any) =>
            c.id !== it.id && (!c.coType || c.coType === "internal"),
        )
        .map((c: any) => ({
          name: pname(c),
          schedule: normalizeWeeklySchedule(c.weeklySchedule, schedAxis?.slots),
        }));
      const onSchedChange = (nws: any) => {
        const nco = collaborators.map((c: any) =>
          c.id === it.id ? { ...c, weeklySchedule: nws } : c,
        );
        persist(SK.co, nco, setCollaborators);
      };
      return (
        <div className="detail-card">
          <h4>
            {t("list.section.weekly_schedule", {
              fallback: "Weekly Schedule",
            })}
          </h4>
          <ScheduleGrid
            schedAxis={schedAxis}
            schedule={normalizeWeeklySchedule(it.weeklySchedule, schedAxis?.slots)}
            onChange={onSchedChange}
            otherSchedules={others}
            readOnly={!canEditSched}
            canUnlock={canEditSched}
            scheduleId={"co-" + it.id}
          />
        </div>
      );
    })(),
  internalRatesCard: (it: any, { _appPerms, t }: any) =>
    canEditInternalRates(_appPerms) &&
    (() => {
      const rates = (
        Array.isArray(it.internalRates) ? it.internalRates : []
      )
        .filter((r: any) => r && r.startDate)
        .slice()
        .sort((a: any, b: any) => b.startDate.localeCompare(a.startDate));
      return (
        <div className="detail-card">
          <h4>
            {t("list.section.internal_rates_history", {
              fallback: "Internal Rates History",
            })}
          </h4>
          {rates.length === 0 ? (
            <p
              style={{
                fontSize: FS.s11,
                color: "var(--tm)",
                fontStyle: "italic",
                padding: "6px 0",
                margin: 0,
              }}
            >
              {t("list.no_internal_rates", {
                fallback: "No internal rates recorded.",
              })}
            </p>
          ) : (
            <Table
              style={{
                width: "100%",
                fontSize: FS.s11,
                borderCollapse: "collapse",
                marginTop: 4,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "4px 6px",
                      fontSize: FS.s9,
                      color: "var(--tm)",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontWeight: 600,
                      borderBottom: "1px solid var(--br)",
                    }}
                  >
                    {t("list.rates.col.start_date", {
                      fallback: "Start Date",
                    })}
                  </th>
                  <th
                    style={{
                      textAlign: "right",
                      padding: "4px 6px",
                      fontSize: FS.s9,
                      color: "var(--tm)",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontWeight: 600,
                      borderBottom: "1px solid var(--br)",
                    }}
                  >
                    {t("list.rates.col.internal_rate", {
                      fallback: "Internal Rate (EUR/h)",
                    })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r: any, i: any) => {
                  const isCurrent = i === 0;
                  return (
                    <tr
                      key={i}
                      style={{
                        background: isCurrent
                          ? "var(--as)"
                          : "transparent",
                      }}
                    >
                      <td
                        style={{
                          padding: "5px 6px",
                          fontFamily: "'JetBrains Mono',monospace",
                          color: isCurrent
                            ? "var(--ac)"
                            : "var(--tm)",
                          fontWeight: isCurrent ? 600 : 400,
                          borderBottom:
                            "1px solid rgba(255,255,255,.04)",
                        }}
                      >
                        {fd(r.startDate)}
                        {isCurrent && (
                          <span
                            style={{
                              fontSize: FS.s8,
                              marginLeft: 6,
                              padding: "1px 5px",
                              borderRadius: RAD.r6,
                              background: "var(--ac)",
                              color: CT.c_fff,
                              fontWeight: 600,
                            }}
                          >
                            {t("list.rates.current", {
                              fallback: "CURRENT",
                            })}
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: "5px 6px",
                          fontFamily: "'JetBrains Mono',monospace",
                          textAlign: "right",
                          color: isCurrent
                            ? "var(--ac)"
                            : "var(--tm)",
                          fontWeight: isCurrent ? 600 : 400,
                          borderBottom:
                            "1px solid rgba(255,255,255,.04)",
                        }}
                      >
                        {fc(+r.rate || 0)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </div>
      );
    })(),
};

export const buildEcfg = ({
  clients,
  opponents,
  opponentLawyers,
  experts,
  jurisdictions,
  collaborators,
  contactPersons,
  opponentContacts,
  mediators,
  arbiters,
  additionalOpponents,
  // 59 §2 / 45c — the `client_type` vocabulary, raw entries (not the display
  // list), because 05m step C's matcher wants the entry and its five facets.
  // App owns the hook; this file is hookless and so is EntityList.
  clientTypeEntries,
  /* 214 §4/§6 — the contact_role and bar vocabularies, raw entries, for a contact's record (the role's word,
     the bar's) — App owns the hooks, as for clientTypeEntries. */
  contactRoleEntries,
  barEntries,
  // 189 §B — the custom-field columns (src/lib/customFieldColumns.tsx), built
  // by App from the `custom_field` definitions it loads; absent → none, which
  // is what the 57c freeze's fixture passes.
  customFieldColsCl,
  /* 195 §C.3a [RULED — Laurent, 2026-09-18: "ditch it everywhere, just keep
     the ones that work, autonomously"] — EACH LIST OWNS ITS SEARCH BOX. The
     page-level box App drew above every section is gone (§C.3e); these
     thirteen declare a `toolbar.search` in matterListCfg.tsx's shape
     (`search: { value, onChange }`), which EntityList renders as the filter
     row's leading box (158 §8: the box leads the row it filters). The FILTER
     does not move — it stays EntityList's `query` read, matching exactly what
     it matched. App builds `searchCtl(key)`: the text, and its change with
     the list's own page reset. */
  listSearch: listSearchIn,
  _appPerms,
  _appIsAdmin,
}: any): Record<string, any> => {
  /* the one search text and its change, read ONCE through a typed local (the
     seam): `change` takes the list's key and the text as a tuple — the house's
     function-prop shape (`Dispatch<[…]>`), so thirteen typed `searchOf(key)`
     calls cost the ratchet nothing */
  /* a host that hands no control has forgotten the thirteen's box: refused
     with a sentence, never a screen without it (the 57c freeze harness hands
     a stable stub) */
  if (!listSearchIn || typeof listSearchIn !== "object") throw new Error("buildEcfg: `listSearch` ({ value, change }) is required — the thirteen lists own their search box (195 §C.3a)");
  const listSearch = listSearchIn as { value: string; change: Dispatch<[string, string]> };
  const searchOf = (key: string) => ({
    value: listSearch.value,
    onChange: (v: string) => {
      listSearch.change([key, v]);
    },
  });
  return {
    cl: {
      toolbar: { search: searchOf("cl") }, // 195 §C.3a — the list's own box
      items: clients,
      et: "cl",
      am: "cl",
      // 168B GLANCE 4 — the eight columns' own cell cap (App.css, where the
      // measurement that chose it is written down). The matter lists pass
      // `elist-matters` through the same prop for the opposite reason.
      tableClass: "elist-cl",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            /* 168 — THE PERSON'S PROSPECT TAG, WHERE A PERSON IS READ.
               The cell itself is `clientNameCell` (src/lib/clientNameCell.tsx),
               which is where its whole argument is written down. It is a
               function this column NAMES rather than a renderer written here,
               so the pill's markup is typed and this file's JSX count does not
               move for a branch none of the frozen screens takes. */
            r: clientNameCell,
          },
          /* 59 §2 / register 45c — A READ BUG, NOT A TRANSLATION. `client_type`
             is a declared, EDITOR-ENABLED taxonomy whose codes are `company`
             and `individual`; this column had no `r`, so EntityList printed
             `it.type` raw and the Clients screen said `company` — in every
             language, and STILL said `company` after a firm renamed the entry
             to "Société" in the taxonomy editor. The vocabulary is not
             translated here and no code is: the stored value is resolved
             through 05m step C's ONE matcher, which falls back to the value
             itself so an orphan code stays visible. The other eight
             taxonomy-backed columns are `bar` (label-as-value, legible today)
             and are 05m's, not this slice's. */
          {
            k: "type",
            l: "Type",
            tg: 1,
            r: (i: { type?: string }) => (
              <span className="tag">{resolveTaxonomyLabel(i.type, clientTypeEntries)}</span>
            ),
          },
          /* 168B §1b (RULING 1) — CASES ARE NAMES, NOT A COUNT. Until now the
             Clients list ended in the GENERIC trailing column every entity
             gets: `cf2("cl", id).length + caseCountExtra`, a number. Ruling 1
             asks for the matters themselves, so `cl` declares the column and
             DECLINES the generic one (`casesCol: false` below) rather than the
             component learning what a client is.
             The count stays, as a small muted prefix — it is the same number
             the generic column printed, over the same two sets (`titles` is
             the regular matters' titles then the collective ones', built by
             `clientMatterSummary`, `src/lib/clientMatters.mjs:158`). The cell
             ellipses at 28ch by `.elist tbody td`'s own rule (App.css), and
             the full list is the `title`, which is what makes the ellipsis
             answerable without opening the row. */
          /* NO `lk`: the header key is the DERIVED `list.cl.col.<k>`, which is
             the convention every other column here follows and — measured —
             the only one the i18n gates can see. `scripts/lint/i18n-total.js`
             and `i18n-keys-complete.js` resolve `t(\`list.${et}.col.${c.k}\`)`
             from the declared column universe; an `lk` override is invisible to
             both, so a column that used one would ship unseeded and silently
             English. Surfaced, not fixed — the matter lists' own `lk` columns
             have the same blind spot. */
          {
            k: "_cases",
            l: "Cases",
            r: (_i: unknown, _c: unknown, s: ClientSummary) => {
              const titles = (s && s.titles) || [];
              if (!titles.length) return "—";
              const all = titles.join(", ");
              return (
                <span title={all}>
                  <span style={{ color: "var(--tm)" }}>{titles.length}</span>
                  {" \u00b7 " + all}
                </span>
              );
            },
            sv: (_i: unknown, _c: unknown, s: ClientSummary) =>
              (s && s.titles[0]) || null,
          },
          /* 168B §1c (RULING 1) — THE CLIENT'S STANDING, not a matter's status.
             `clientMatterSummary` picks the client's MOST RECENT standing
             across their matters by 141 §2.3's one rule — their own link status
             where the link carries one, the matter's own status otherwise — and
             now carries out WHICH branch answered and on which matter, because
             a bare string cannot be drawn.
             The cell is the client record's own: `clientStandingPill`, the
             three arms Laurent ruled on 2026-08-27, mounted rather than
             redrawn. The PERSON's prospect tag is a different mark and stays on
             the Name cell (168); the arm here is the MATTER's (140 §1.2). */
          {
            k: "_clientStatus",
            l: "Client status",
            r: (i: ClientTagRow, ctx: ListCtx, s: ClientSummary) =>
              clientStandingPill({
                standing: s ? s.mostRecentStanding : null,
                // The PERSON's tag first, the winning matter's after it — one
                // function answers it, and the filter below asks the same one.
                isProspect: clientStatusShown(i, s).prospect,
                t: ctx.t,
                inheritedLabel: caseStatusLabel(
                  ctx.caseStatusesList,
                  s && s.mostRecentStanding ? s.mostRecentStanding.status : null,
                ),
              }) || "—",
            /* PROSPECT FIRST [RULED Laurent 2026-09-05], then the ladder, then
               the taxonomy. A row the column shows as Prospect — by the
               person's tag or the winning matter's — ranks where the value
               Prospect ranks, which is 0, because `KNOWN_STATUSES` is ordered
               Prospect → Archived and the sort must agree with what is drawn.
               A status INHERITED from the matter ranks after all five, in the
               `case_status` taxonomy's own order. A value in neither list sorts
               last — which is what a null `sv` means (§1a). */
            sv: (i: ClientTagRow, ctx: ListCtx, s: ClientSummary) => {
              const shown = clientStatusShown(i, s);
              if (shown.prospect)
                return (KNOWN_STATUSES as string[]).indexOf("Prospect");
              const st = s ? s.mostRecentStanding : null;
              if (!st || !st.status) return null;
              if (st.fromLink) {
                const k = (KNOWN_STATUSES as string[]).indexOf(st.status);
                return k < 0 ? null : k;
              }
              const j = (ctx.caseStatusesList || []).findIndex(
                ([v]) => v === st.status,
              );
              return j < 0 ? null : (KNOWN_STATUSES as string[]).length + j;
            },
          },
          /* 168B §1d (RULING 1) — WHEN THAT STANDING WAS SET. The companion
             date `clientMatterSummary` already ranks by: the link's own
             lifecycle slot where the standing came from a link, the matter's
             status-companion column otherwise (141 §2.1/§2.3).
             MEASURED, AND THE BRIEF'S PARENTHESIS IS WRONG: `fd`
             (`src/utils.ts:503`) is `toLocaleDateString` with 2-digit day and
             month, so it renders 05/09/2026, not an ISO day. The format asked
             for is `yyyy-mm-dd`, and that is what BOTH surfaces showing this
             value already print — the `_xstatusdate` column this replaces and
             `ClientCasesCard`'s own `fmtDate`, each `toISOString().slice(0,10)`.
             The expression is kept rather than improved, UTC slice included, so
             the column reads exactly as the cell it replaces did. */
          {
            k: "_statusDate",
            l: "Status date",
            r: (_i: unknown, _c: ListCtx, s: ClientSummary) =>
              s && s.mostRecentStatusDate
                ? s.mostRecentStatusDate.toISOString().slice(0, 10)
                : "—",
            sv: (_i: unknown, _c: ListCtx, s: ClientSummary) =>
              (s && s.mostRecentStatusDate) || null,
          },
          /* 168B §1e (RULING 1) — WHAT THIS CLIENT OWES, permanently and
             sortably. The same sum the filter-driven `_xdue` column computed,
             under the same gate and with no arithmetic changed: Σ
             `matterAmountDue` over the client's regular and collective matters,
             each one only if `canSeeMatterDue` says so, and the served
             `amountDue` is the authority (P17.1c).
             THE COLUMN IS ABSENT, NOT BLANK, for a viewer who may see no
             figures at all — `gate` removes it from the header, the rows and
             the separators' colSpan. A viewer who may see SOME matters gets the
             sum over those, and the muted label where they may see none of that
             client's, exactly as before. */
          {
            k: "_due",
            l: "Amount due",
            gate: (ctx: ListCtx) => !!ctx.canSeeAnyDue,
            r: (_i: unknown, ctx: ListCtx, s: ClientSummary) =>
              s && s.dueSeen ? (
                /* 169 R7 — one line per other currency beneath the firm's
                   figure; a client with none renders `fc(dueSum)` alone. */
                s.dueForeign ? <MoneyStack home={s.dueSum} foreign={s.dueForeign} /> : fc(s.dueSum)
              ) : (
                <span style={{ fontStyle: "italic", color: "var(--tm)" }}>
                  {ctx.t("amount.due.label", { fallback: "Amount due" })}
                </span>
              ),
            sv: (_i: unknown, _c: ListCtx, s: ClientSummary) =>
              s && s.dueSeen ? s.dueSum : null,
          },
          /* 168B §1f (RULING 2) — AN ADDRESS OPENS A DRAFT AND A NUMBER
             DIALS, through what the app already has.
             The e-mail goes through `openCompose` (`src/lib/outlookCompose.ts`),
             which is the firm's mail-client preference — Outlook web, Gmail, or
             the `mailto:` fallback — so the row obeys the same setting the case
             view's compose does. The `href` is the honest `mailto:` beneath it,
             for a middle-click or a context menu; the handler prevents it and
             routes through the preference instead.
             The phone is the shape the jurisdictions' Tel column already has
             (`_tel` below): a `tel:` anchor over the number with its spaces
             stripped. MEASURED: this app's own calling is INTERNAL ONLY —
             `src/lib/webrtcCall.ts:1` is a mesh between collaborators — so a
             `tel:` hands the number to the machine's phone app (on Laurent's
             Mac, the handler macOS has registered), not to LexCRM.
             BOTH STOP PROPAGATION, because the row's own click opens the
             client's detail pane (`EntityList.tsx`, the `<tr onClick>`). */
          {
            k: "email",
            l: "Email",
            r: (i: ClientContactRow, ctx: ListCtx) =>
              i.email ? (
                <span
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  <a
                    href={"mailto:" + i.email}
                    // 168B GLANCE 4 — the cap ellipses the longest addresses,
                    // so the whole one stays readable on hover, as the Cases
                    // cell's title does.
                    title={String(i.email)}
                    style={{ color: "var(--ac)" }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (ctx.composeTo) ctx.composeTo(String(i.email));
                    }}
                  >
                    {i.email}
                  </a>
                  {copyAff(ctx, String(i.email))}
                </span>
              ) : (
                "—"
              ),
          },
          {
            k: "phone",
            l: "Phone",
            r: (i: ClientContactRow, ctx: ListCtx) =>
              i.phone ? (
                <span
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  <a
                    href={"tel:" + String(i.phone).replace(/\s+/g, "")}
                    className="mono"
                    style={{ color: "var(--ac)" }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {i.phone}
                  </a>
                  {copyAff(ctx, String(i.phone))}
                </span>
              ) : (
                "—"
              ),
          },
          // 189 §B — after the built-in columns, in the definitions' order.
          ...((customFieldColsCl as CustomFieldColumn[] | undefined) ?? []),
        ],
        d: [
          { k: "type", l: "Type", r: (i: { type?: string }) => resolveTaxonomyLabel(i.type, clientTypeEntries) },
          {
            k: "lang",
            l: "Language",
            /* 207C §6 — the language STORED (it read `lang`, which the server never
               sends: every client read « French »), in the reader's words — the
               client form's own `lang.*` rows (it printed English in every
               language). 209 §5 [RULED 18] — a client's own language stays
               optional: « — » when none (it read French). */
            r: (i: unknown) => {
              const code = storedClientLanguage(i);
              if (!code) return "—";
              const word = LANGUAGE_WORDS[code];
              return word ? _tImp(word[0], { fallback: word[1] }) : code;
            },
          },
          {
            k: "nationalRegisterId",
            l: "National Register No.",
            r: (i: any) =>
              i.type === "individual" ? i.nationalRegisterId || "—" : null,
          },
          /* 211 §21 [RULED 24] — the client's own references, above Quantum in the form's order,
             each shown only when filled, each in the form's own words (§16's key, field.former_case_ref). */
          { k: "contractNumber", l: "Ref. contract", lk: "field.client_contract_ref", mo: 1, show: (i: { contractNumber?: unknown }) => typeof i.contractNumber === "string" && i.contractNumber.trim() !== "" },
          { k: "formerCaseRef", l: "Former Case Ref", lk: "field.former_case_ref", show: (i: { formerCaseRef?: unknown }) => typeof i.formerCaseRef === "string" && i.formerCaseRef.trim() !== "" },
          { k: "quantum", l: "Quantum", r: (i: any) => fq(i.quantum) },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "_addr", l: "Address", r: faddr },
          { k: "vat", l: "VAT", mo: 1 },
          { k: "notes", l: "Notes" },
        ],
      },
      label: ENTITY_NOUNS.cl,
      // Phase 36 (D) — clients use the guarded delete (refuse + offer
      // archive); other entities declare the generic engine del.
      onDelete: (it: any, { delClient }: any) => delClient(it.id),
      // 206C §4.1 — the chip moved to clientStandingPill.tsx, unchanged, so the
      // provisioning list can wear it too.
      detailBadge: (it: any, { t }: any) => it.archived && clientArchivedChip(t),
      /* Phase 36 (D) — client archive / unarchive (reversible). */
      archiveAction: (it: any, { doArchiveClient, doUnarchiveClient, t }: any) => (
        <button
          className="btn btn-ghost"
          style={{ padding: "3px 7px", fontSize: FS.s11 }}
          onClick={() =>
            it.archived
              ? doUnarchiveClient(it.id)
              : doArchiveClient(it.id)
          }
        >
          {it.archived
            ? t("common.unarchive", { fallback: "Unarchive" })
            : t("common.archive", { fallback: "Archive" })}
        </button>
      ),
      /* 189 §H — WRITE TO THIS CLIENT WITH A TEMPLATE, from their screen:
         opens ClientMailTemplateModal (App holds the state) — a firm template
         merged for this person, an Outlook draft in the user's own mailbox.
         Ruling 189 (7): no consent is consulted; the button shows whether the
         person refused campaigns or not. */
      detailActions: (it: MailTemplateClient, ctx: ListCtx) => (
        <button
          className="btn btn-ghost"
          style={{ padding: "3px 7px", fontSize: FS.s11 }}
          onClick={() => { if (ctx.openMailTemplate) ctx.openMailTemplate(it); }}
        >
          {ctx.t("clientMail.action", { fallback: "Write with a template" })}
        </button>
      ),
      // Phase 43 (item 3 / D22) — enriched, archived-inclusive matter list
      // with per-matter status + pro-forma amount due and a client total.
      // Stable child so its per-client fetch/state survive App re-renders
      // (the EList detail branch cannot hold hooks).
      casesCard: (it: any, { canSeeMatterDue, caseStatusesList, cases, collectiveCases, invoices, nav }: any) => (
        <ClientCasesCard
          client={it}
          ca={cases}
          cc={collectiveCases}
          inv={invoices}
          nav={nav}
          fc={fc}
          canSeeMatterDue={canSeeMatterDue}
          statusLabel={(code: any) => {
            const e = (caseStatusesList || []).find(([v]: any) => v === code);
            return e ? e[1] : code;
          }}
        />
      ),
      contactPersonsCard: (it: any, { contactPersons, nav, setDetailId, t }: any) =>
        (() => {
          const linked = contactPersons.filter((p: any) => p.clientId === it.id);
          return (
            <div className="detail-card">
              <h4>
                {t("list.section.contact_persons", {
                  fallback: "Contact Persons",
                })}
              </h4>
              {linked.length === 0 ? (
                <p
                  style={{
                    fontSize: FS.s11,
                    color: "var(--tm)",
                    fontStyle: "italic",
                    margin: 0,
                  }}
                >
                  {t("list.no_linked_contacts", {
                    fallback: "No contact persons linked.",
                  })}
                </p>
              ) : (
                <div>
                  {linked.map((p: any) => (
                    <div
                      key={p.id}
                      onClick={() => {
                        setDetailId(null);
                        nav("cp", p.id);
                      }}
                      style={{
                        padding: "5px 6px",
                        borderBottom: "1px solid rgba(255,255,255,.04)",
                        cursor: "pointer",
                        fontSize: FS.s11,
                      }}
                    >
                      <div
                        style={{ color: "var(--tp)", fontWeight: 500 }}
                      >
                        → {pname(p)}
                      </div>
                      {(p.function || p.email) && (
                        <div style={{ color: "var(--tm)", fontSize: FS.s10 }}>
                          {[p.function, p.email]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })(),
      // 182 A.3 — the person's contact timeline, beside the Cases card and
      // before the CDD footer: the person's own notes ∪ the notes of the
      // matters they are linked to (includeMatters), each with its matter
      // pill. The record's own gate: lateral.persons.view to see it,
      // lateral.persons.edit to write. Stable child (the ClientCasesCard rule).
      notesCard: (it: any, { _appPerms, _appIsAdmin, currentUser }: any) =>
        _appPerms?.can?.("lateral.persons.view") ? (
          <InteractionSection
            chrome="card"
            anchorId="cl-notes"
            clientId={it.id}
            includeMatters
            viewerId={currentUser?.id}
            isAdmin={_appIsAdmin === true}
            canCreate={!!_appPerms?.can?.("lateral.persons.edit")}
            canEdit={!!_appPerms?.can?.("lateral.persons.edit")}
          />
        ) : null,
      detailFooter: (it: any, { collaborators }: any) => (
        <CddClientSection client={it} co={collaborators} />
      ),
      // Phase 43 (item 1) — archived visibility: the explicit clFilter.arch
      // axis (active | incl | only), replacing the implicit hide-until-typed
      // rule so archived former clients can be surfaced for a manual conflict
      // check.
      archivedVisibility: (i: any, { clFilter }: any) => {
        if (clFilter.arch === "only" && !i.archived) return false;
        if (clFilter.arch === "active" && i.archived) return false;
        return true;
      },
      /* Phase 43 (item 1) — Clients-scoped structured filter predicate. Direct
         fields match by substring or by code; derived axes come from
         clientMatterSummary over the loaded ca/cc/inv, and are only computed
         when at least one derived axis is active (keeps the default list
         cheap).
         168B §2b (RULING 3) — three changes, one of them a defect.
         · NEW `type`: the `client_type` code, exact, over the same vocabulary
           the Type column resolves through.
         · NEW `clientStatus`: the client's STANDING, matched against
           `mostRecentStatus` — the value the Client status column shows.
         · FIXED `status` (Case status). MEASURED BEFORE WRITING, as §2b asks:
           it compared `sum.mostRecentStatus` — the STANDING, which since
           141 §2.3 may be a LINK value like "Active" — against a `case_status`
           CODE like "active". A client whose most recent standing came from a
           link could therefore NEVER match this filter, silently. Within
           ruling 3 the fix is to ask the matters themselves: Case status
           matches when ANY of the client's matters carries that status,
           independent of the standing.
         · `email` is gone with its input (ruling 3's enumeration). */
      // 211 §19 — the search finds a client by its « Réf. contrat » (a field, as today) and by any of its numbers in any case
      searchText: (i: { id?: number }, { collectiveCases }: { collectiveCases?: unknown }) => numbersShown(numbersByClient(collectiveCases).get(Number(i.id)) ?? []),
      rowPass: (i: any, { cases, clFilter, collectiveCases, invoices }: any) => {
        const f = clFilter;
        const inc = (hay: any, needle: any) =>
          (hay || "").toLowerCase().includes(needle.toLowerCase().trim());
        if (f.name && !inc(pname(i), f.name)) return false;
        if (f.type !== "all" && (i.type || "") !== f.type) return false;
        if (f.country && !inc(i.country, f.country)) return false;
        const needMatters =
          f.caseLink !== "all" ||
          f.caseName ||
          f.status !== "all" ||
          f.clientStatus !== "all" ||
          f.statusFrom ||
          f.statusTo ||
          f.due !== "all";
        if (!needMatters) return true;
        const sum = clientMatterSummary(i, cases, collectiveCases, invoices);
        if (f.caseLink === "yes" && !sum.hasCaseLink) return false;
        if (f.caseLink === "no" && sum.hasCaseLink) return false;
        if (f.caseName && !inc(sum.titles.join(" | "), f.caseName)) return false;
        if (f.status !== "all" && !sum.matterStatuses.includes(f.status))
          return false;
        /* THE FILTER MATCHES WHAT THE COLUMN SHOWS [RULED Laurent 2026-09-05,
           measured on staging: filtering on Prospect returned NOBODY]. It read
           `mostRecentStatus`, and since the same ruling the column no longer
           shows that for a tagged person — it shows Prospect, because the
           person-level tag is the stronger statement. So both ask
           `clientStatusShown`: "Prospect" finds tagged persons AND members
           whose standing is Prospect, and the other four match the standing of
           untagged persons only. */
        if (
          f.clientStatus !== "all" &&
          clientStatusShown(i, sum).code !== f.clientStatus
        )
          return false;
        if (f.statusFrom || f.statusTo) {
          const d: any = sum.mostRecentStatusDate;
          if (!d) return false;
          if (f.statusFrom && d < new Date(f.statusFrom)) return false;
          if (f.statusTo && d > new Date(f.statusTo + "T23:59:59")) return false;
        }
        if (f.due === "yes" && !sum.hasDue) return false;
        if (f.due === "no" && sum.hasDue) return false;
        return true;
      },
      /* Phase 43 CORRECTION (item 8) — dynamic columns: surface the axis
         actually being filtered on, so filtered rows are distinguishable.
         168B §1g — THREE OF THE FOUR ARE NOW PERMANENT COLUMNS and have left
         this list: case-name is §1b's Cases column, status-date is §1d's,
         amount-due is §1e's. The fourth, country, is REMOVED BY RULING 1 —
         "Country is never a column; it stays a filter" — so a filtered
         Clients list no longer grows a Country column beside the eight.
         What remains is the CASE status, which is a different thing from the
         client's standing (§1c's column) and stays filter-driven. */
      extraCols: ({ caseStatusesList, clFilter, t }: any) => {
        return [
          clFilter.status !== "all" && {
            k: "_xstatus",
            label: t("cl.col.case_status", { fallback: "Case status" }),
            // 168B §2b — the MATTERS' statuses, which is what the axis above
            // now matches on. It showed `mostRecentStatus` — the standing —
            // which is the very confusion the predicate fix names.
            cell: (it: any, s: any) =>
              (s.matterStatuses || [])
                .map((x: string) => caseStatusLabel(caseStatusesList, x))
                .join(", ") || "—",
          },
        ].filter(Boolean);
      },
      // 168B §1b — WAS `extraRowCtx`. EntityList computes it once per row per
      // render and hands it to every column, not only to the extra ones.
      rowCtx: (
        it: any,
        { canSeeAnyDue, canSeeMatterDue, cases, collectiveCases, invoices }: any,
      ) => {
        const sum = clientMatterSummary(it, cases, collectiveCases, invoices);
        // 168B §1e — the per-matter gate, applied once per row. Skipped whole
        // when the viewer may see no figures, because then the column is gone.
        let dueSum = 0;
        let dueSeen = false;
        // 169 R7 — the other currencies' dues, per code (null when none).
        let dueForeign: Record<string, number> | null = null;
        if (canSeeAnyDue) {
          for (const c of clientRegularCases(it.id, cases))
            if (canSeeMatterDue(c.id, null)) {
              dueSeen = true;
              dueSum += matterAmountDue(c.id, "caseId", invoices);
              dueForeign = mergeForeign(dueForeign, matterAmountDueForeign(c.id, "caseId", invoices));
            }
          for (const c of clientCollectiveCases(it.id, collectiveCases))
            if (canSeeMatterDue(null, c.id)) {
              dueSeen = true;
              dueSum += matterAmountDue(c.id, "ccId", invoices);
              dueForeign = mergeForeign(dueForeign, matterAmountDueForeign(c.id, "ccId", invoices));
            }
        }
        return { ...sum, dueSeen, dueSum, dueForeign };
      },
      // 168B §1b — `cl` draws its own Cases column, so it declines the generic
      // trailing one. `caseCountExtra` went with it: it existed to add the
      // collective memberships to that column's number, and `titles` already
      // spans both sets.
      casesCol: false,
    },
    op: {
      toolbar: { search: searchOf("op") }, // 195 §C.3a — the list's own box
      items: opponents,
      et: "op",
      am: "op",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          /* 59f — THE OPPONENT TYPE PRINTED ITS CODE (`company`, and `natural`
             on 24 of the 26 rows here). 45c's census could not see it: it
             crossed ecfg's columns with TAXONOMY_REFERENCES, and this column is
             not declared there — `census-rendered-codes.js` asks the ROWS
             instead and reports it ORPHAN, because NO taxonomy kind contains
             these values.

             So it does NOT resolve like the client column beside it. The client
             type IS a taxonomy and its editor offers the taxonomy list; the
             opponent type is a FIXED PAIR written by a hardcoded two-option
             select (`App.tsx:7176`, `:7770`) that labels them `type.company` /
             `type.individual`. **The renderer resolves the way the editor
             writes** — through those same two keys — and an unknown code falls
             through to itself, so `natural` stays visible rather than being
             swallowed. */
          { k: "type", l: "Type", tg: 1, r: (i: { type?: string }) => (
            <span className="tag">{opponentTypeLabel(i.type)}</span>
          ) },
          { k: "contact", l: "Contact" },
        ],
        d: [
          { k: "type", l: "Type", r: (i: { type?: string }) => opponentTypeLabel(i.type) },
          { k: "contact", l: "Contact" },
          { k: "notes", l: "Notes" },
        ],
      },
      label: ENTITY_NOUNS.op,
      onDelete: (it: any, { del, opponents, setOpponents }: any) =>
        del(SK.op, opponents, setOpponents, it.id),
      opponentContactsCard: (it: any, { nav, opponentContacts, setDetailId, t }: any) =>
        (() => {
          const linked = opponentContacts.filter((c: any) => c.opponentId === it.id);
          return (
            <div className="detail-card">
              <h4>
                {t("list.section.opponent_contacts", {
                  fallback: "Opponent Contacts",
                })}
              </h4>
              {linked.length === 0 ? (
                <p
                  style={{
                    fontSize: FS.s11,
                    color: "var(--tm)",
                    fontStyle: "italic",
                    margin: 0,
                  }}
                >
                  {t("list.no_linked_contacts", {
                    fallback: "No contact persons linked.",
                  })}
                </p>
              ) : (
                <div>
                  {linked.map((c: any) => (
                    <div
                      key={c.id}
                      onClick={() => {
                        setDetailId(null);
                        nav("oc", c.id);
                      }}
                      style={{
                        padding: "5px 6px",
                        borderBottom: "1px solid rgba(255,255,255,.04)",
                        cursor: "pointer",
                        fontSize: FS.s11,
                      }}
                    >
                      <div
                        style={{ color: "var(--tp)", fontWeight: 500 }}
                      >
                        → {pname(c)}
                      </div>
                      {(c.function || c.email) && (
                        <div style={{ color: "var(--tm)", fontSize: FS.s10 }}>
                          {[c.function, c.email]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })(),
    },
    ocu: {
      toolbar: { search: searchOf("ocu") }, // 195 §C.3a — the list's own box
      items: opponentLawyers,
      et: "ocu",
      am: "ocu",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          {
            k: "firm",
            l: "Firm",
            r: (i: any) => (
              <span style={{ fontWeight: 500, color: "var(--tp)" }}>
                {i.firm || "—"}
              </span>
            ),
          },
          { k: "bar", l: "Bar" },
          { k: "email", l: "Email" },
        ],
        d: [
          { k: "firm", l: "Firm" },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "bar", l: "Bar" },
          { k: "_addr", l: "Address", r: faddr },
          { k: "notes", l: "Notes" }, // 214 §1 (a) — the notes the form asks are shown where they were lost
        ],
      },
      label: ENTITY_NOUNS.ocu,
      /* 214 §6 [RULED 52] — the counsel's matters: every matter he is linked to, the party he represents there,
         acting or suspended since a date, the acting ones first (src/views/PartyMattersCard.tsx). Typed at the
         seam: this file's `any` count may not rise. */
      casesCard: (it: unknown, ctx: unknown) => {
        const c = ctx as { cases: readonly unknown[]; collectiveCases: readonly unknown[]; opponents: readonly unknown[]; clients: readonly unknown[]; nav: NavFn };
        return <PartyMattersCard kind="ocu" item={it as { id: number; clientId?: number | null }} cases={c.cases} collectiveCases={c.collectiveCases} opponents={c.opponents} clients={c.clients} nav={c.nav} />;
      },
      onDelete: (it: any, { del, opponentLawyers, setOpponentLawyers }: any) =>
        del(SK.ocu, opponentLawyers, setOpponentLawyers, it.id),
    },
    ex: {
      toolbar: { search: searchOf("ex") }, // 195 §C.3a — the list's own box
      items: experts,
      et: "ex",
      am: "ex",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          { k: "spec", l: "Specialty" },
          {
            k: "office",
            l: "Office",
            r: (i: any) => (
              <span style={{ fontWeight: 500, color: "var(--tp)" }}>
                {i.office || "—"}
              </span>
            ),
          },
          { k: "email", l: "Email" },
        ],
        d: [
          { k: "spec", l: "Specialty" },
          { k: "office", l: "Office" },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "professionalRegistration", l: "Professional Registration" },
          { k: "_addr", l: "Address", r: faddr },
          { k: "notes", l: "Notes" },
        ],
      },
      label: ENTITY_NOUNS.ex,
      onDelete: (it: any, { del, experts, setExperts }: any) =>
        del(SK.ex, experts, setExperts, it.id),
    },
    ju: {
      toolbar: { search: searchOf("ju") }, // 195 §C.3a — the list's own box
      items: jurisdictions,
      et: "ju",
      am: "ju",
      cols: {
        t: [
          { k: "name", l: "Court" },
          { k: "city", l: "City" },
          { k: "tel", l: "Tel", mo: 1 },
        ],
        d: [
          { k: "_addr", l: "Address", r: faddr },
          {
            k: "_tel",
            l: "Tel",
            r: (i: any) =>
              i.tel ? (
                <a
                  href={`tel:${i.tel.replace(/\s+/g, "")}`}
                  className="mono"
                  style={{ color: "var(--ac)" }}
                >
                  {i.tel}
                </a>
              ) : (
                "—"
              ),
          },
          {
            k: "_email",
            l: "Email",
            r: (i: any) =>
              i.email ? (
                <a href={`mailto:${i.email}`} style={{ color: "var(--ac)" }}>
                  {i.email}
                </a>
              ) : (
                "—"
              ),
          },
        ],
      },
      label: ENTITY_NOUNS.ju,
      onDelete: (it: any, { del, jurisdictions, setJurisdictions }: any) =>
        del(SK.ju, jurisdictions, setJurisdictions, it.id),
    },
    co2int: {
      toolbar: { search: searchOf("co2int") }, // 195 §C.3a — the list's own box
      items: collaborators.filter((c: any) => !c.coType || c.coType === "internal"),
      et: "co",
      am: "co",
      ...CO_FACTS,
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: coNameCell,
          },
          { k: "role", l: "Role", tg: 1 },
          {
            k: "defaultRatio",
            l: "Ratio",
            r: (i: any) => (
              <span className="mono">
                {i.defaultRatio ?? i.roleDefaultRatio ?? roleDefaultRatio(i.role) ?? 100}%
              </span>
            ),
          },
          ...(canEditInternalRates(_appPerms)
            ? [
                {
                  k: "_curRate",
                  l: "Internal Rate",
                  r: (i: any) => {
                    const r = getInternalRate(
                      i,
                      new Date().toISOString().slice(0, 10),
                    );
                    return r > 0 ? (
                      <span className="mono">{fc(r)}/h</span>
                    ) : (
                      <span style={{ color: "var(--tm)" }}>—</span>
                    );
                  },
                },
              ]
            : []),
          ...(_appIsAdmin || _appPerms.can("caseView.terms.view")
            ? [
                {
                  k: "_wkh",
                  /* RULED by Laurent 2026-08-10: RENAMED, not merely
                     translated. The column reads `weeklySchedule`, and
                     "Wk Hours" describes hours where the thing is a schedule. */
                  l: "Week schedule",
                  r: (i: any) => (
                    <span className="mono">{weeklyHours(i.weeklySchedule)}h</span>
                  ),
                },
              ]
            : []),
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
        ],
        d: [
          { k: "role", l: "Role" },
          {
            k: "defaultRatio",
            l: "Default Ratio",
            r: (i: any) => (i.defaultRatio ?? i.roleDefaultRatio ?? roleDefaultRatio(i.role) ?? 100) + "%",
          },
          ...(canEditInternalRates(_appPerms)
            ? [
                {
                  k: "_curRate",
                  l: "Current Internal Rate",
                  r: (i: any) => {
                    const r = getInternalRate(
                      i,
                      new Date().toISOString().slice(0, 10),
                    );
                    return r > 0 ? fc(r) + "/h" : "—";
                  },
                },
              ]
            : []),
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "barAssociation", l: "Bar Association" },
        ],
      },
    },
    co2ext: {
      toolbar: { search: searchOf("co2ext") }, // 195 §C.3a — the list's own box
      items: collaborators.filter((c: any) => c.coType === "external"),
      et: "co",
      am: "co",
      ...CO_FACTS,
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: coNameCell,
          },
          { k: "role", l: "Role", tg: 1 },
          {
            k: "defaultRatio",
            l: "Ratio",
            r: (i: any) => (
              <span className="mono">
                {i.defaultRatio ?? i.roleDefaultRatio ?? roleDefaultRatio(i.role) ?? 100}%
              </span>
            ),
          },
          ...(canEditInternalRates(_appPerms)
            ? [
                {
                  k: "_curRate",
                  l: "Internal Rate",
                  r: (i: any) => {
                    const r = getInternalRate(
                      i,
                      new Date().toISOString().slice(0, 10),
                    );
                    return r > 0 ? (
                      <span className="mono">{fc(r)}/h</span>
                    ) : (
                      <span style={{ color: "var(--tm)" }}>—</span>
                    );
                  },
                },
              ]
            : []),
          ...(_appIsAdmin || _appPerms.can("caseView.terms.view")
            ? [
                {
                  k: "_wkh",
                  /* RULED by Laurent 2026-08-10: RENAMED, not merely
                     translated. The column reads `weeklySchedule`, and
                     "Wk Hours" describes hours where the thing is a schedule. */
                  l: "Week schedule",
                  r: (i: any) => (
                    <span className="mono">{weeklyHours(i.weeklySchedule)}h</span>
                  ),
                },
              ]
            : []),
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
        ],
        d: [
          { k: "role", l: "Role" },
          {
            k: "defaultRatio",
            l: "Default Ratio",
            r: (i: any) => (i.defaultRatio ?? i.roleDefaultRatio ?? roleDefaultRatio(i.role) ?? 100) + "%",
          },
          ...(canEditInternalRates(_appPerms)
            ? [
                {
                  k: "_curRate",
                  l: "Current Internal Rate",
                  r: (i: any) => {
                    const r = getInternalRate(
                      i,
                      new Date().toISOString().slice(0, 10),
                    );
                    return r > 0 ? fc(r) + "/h" : "—";
                  },
                },
              ]
            : []),
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "barAssociation", l: "Bar Association" },
        ],
      },
    },
    co2: {
      toolbar: { search: searchOf("co2") }, // 195 §C.3a — the list's own box
      items: collaborators,
      et: "co",
      am: "co",
      ...CO_FACTS,
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: coNameCell,
          },
          { k: "role", l: "Role", tg: 1 },
          {
            k: "coType",
            l: "Type",
            r: (i: any) => (
              <span
                style={{
                  fontSize: FS.s9,
                  color: i.coType === "external" ? "var(--am)" : "var(--ts)",
                }}
              >
                {i.coType === "external"
                  ? _tImp("co.type.external", { fallback: "External" })
                  : _tImp("co.type.internal", { fallback: "Internal" })}
              </span>
            ),
          },
          {
            k: "defaultRatio",
            l: "Ratio",
            r: (i: any) => (
              <span className="mono">
                {i.defaultRatio ?? i.roleDefaultRatio ?? roleDefaultRatio(i.role) ?? 100}%
              </span>
            ),
          },
          ...(canEditInternalRates(_appPerms)
            ? [
                {
                  k: "_curRate",
                  l: "Internal Rate",
                  r: (i: any) => {
                    const r = getInternalRate(
                      i,
                      new Date().toISOString().slice(0, 10),
                    );
                    return r > 0 ? (
                      <span className="mono">{fc(r)}/h</span>
                    ) : (
                      <span style={{ color: "var(--tm)" }}>—</span>
                    );
                  },
                },
              ]
            : []),
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
        ],
        d: [
          { k: "role", l: "Role" },
          {
            k: "coType",
            l: "Type",
            /* 59f — the DETAIL half of the badge 59 §3 keyed at the table. It
               was missed because gate 1 sees a string literal that is a JSX
               EXPRESSION CHILD (the badge) and not one RETURNED from an arrow
               (this), so one of the pair was caught and the other was not. Same
               two keys. */
            r: (i: any) =>
              i.coType === "external"
                ? _tImp("co.type.external", { fallback: "External" })
                : _tImp("co.type.internal", { fallback: "Internal" }),
          },
          {
            k: "defaultRatio",
            l: "Default Ratio",
            r: (i: any) => (i.defaultRatio ?? i.roleDefaultRatio ?? roleDefaultRatio(i.role) ?? 100) + "%",
          },
          ...(canEditInternalRates(_appPerms)
            ? [
                {
                  k: "_curRate",
                  l: "Current Internal Rate",
                  r: (i: any) => {
                    const r = getInternalRate(
                      i,
                      new Date().toISOString().slice(0, 10),
                    );
                    return r > 0 ? fc(r) + "/h" : "—";
                  },
                },
              ]
            : []),
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "barAssociation", l: "Bar Association" },
        ],
      },
    },
    cp: {
      toolbar: { search: searchOf("cp") }, // 195 §C.3a — the list's own box
      items: contactPersons,
      et: "cp",
      am: "cp",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          { k: "function", l: "Function", tg: 1 },
          // 214 §4 [RULED 53] — the role's word (the contact_role vocabulary; `contact` for a row without one)
          { k: "role", l: "Role", r: (i: unknown) => String(resolveTaxonomyLabel(roleCodeOf(i as { role?: unknown }), contactRoleEntries) ?? "") },
          { k: "company", l: "Company" },
          { k: "email", l: "Email" },
        ],
        d: [
          { k: "function", l: "Function" },
          { k: "role", l: "Role", r: (i: unknown) => String(resolveTaxonomyLabel(roleCodeOf(i as { role?: unknown }), contactRoleEntries) ?? "") },
          // 214 §4 — the bar, shown for the role Avocat alone (211 §21's `show`)
          { k: "bar", l: "Bar", show: (i: unknown) => isClientCounsel(i as { role?: unknown }), r: (i: unknown) => String(resolveTaxonomyLabel((i as { bar?: unknown }).bar, barEntries) ?? "") },
          { k: "company", l: "Company" },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
        ],
      },
      label: ENTITY_NOUNS.cp,
      /* 214 §6 [RULED 52] — a contact with the role Avocat lists his matters as a counsel does (the client he
         stands for, acting or suspended); any other contact keeps the default card's reference pills. */
      casesCard: (it: unknown, ctx: unknown) => {
        const c = ctx as { t: TFn; lp: LpFn; cases: readonly unknown[]; collectiveCases: readonly unknown[]; opponents: readonly unknown[]; clients: readonly unknown[]; nav: NavFn };
        const row = it as { id: number; clientId?: number | null; role?: unknown };
        if (isClientCounsel(row)) return <PartyMattersCard kind="cp" item={row} cases={c.cases} collectiveCases={c.collectiveCases} opponents={c.opponents} clients={c.clients} nav={c.nav} />;
        return (
          <div className="detail-card">
            <h4>{c.t("list.section.cases", { fallback: "Cases" })}</h4>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>{c.lp("cp", row.id) as React.ReactNode}</div>
          </div>
        );
      },
      onDelete: (it: any, { contactPersons, del, setContactPersons }: any) =>
        del(SK.cp, contactPersons, setContactPersons, it.id),
      linkedClientCard: (it: any, { clients, nav, setDetailId, t }: any) =>
        it.clientId &&
        (() => {
          const parent = clients.find((c: any) => c.id === it.clientId);
          if (!parent) return null;
          return (
            <div className="detail-card">
              <h4>
                {t("list.section.linked_client", {
                  fallback: "Linked Client",
                })}
              </h4>
              <div
                onClick={() => {
                  setDetailId(null);
                  nav("cl", parent.id);
                }}
                style={{
                  padding: "5px 6px",
                  cursor: "pointer",
                  fontSize: FS.s11,
                  color: "var(--tp)",
                  fontWeight: 500,
                }}
              >
                ← {pname(parent)}
                {parent.companyName && parent.vat && (
                  <span
                    style={{
                      color: "var(--tm)",
                      fontWeight: 400,
                      marginLeft: 6,
                      fontSize: FS.s10,
                    }}
                  >
                    {t("field.vat", { fallback: "VAT" })} {parent.vat}
                  </span>
                )}
              </div>
            </div>
          );
        })(),
    },
    oc: {
      toolbar: { search: searchOf("oc") }, // 195 §C.3a — the list's own box
      items: opponentContacts,
      et: "oc",
      am: "oc",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          { k: "function", l: "Function", tg: 1 },
          { k: "company", l: "Company" },
          { k: "email", l: "Email" },
        ],
        d: [
          { k: "function", l: "Function" },
          { k: "company", l: "Company" },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
        ],
      },
      label: ENTITY_NOUNS.oc,
      onDelete: (it: any, { del, opponentContacts, setOpponentContacts }: any) =>
        del(SK.oc, opponentContacts, setOpponentContacts, it.id),
      linkedOpponentCard: (it: any, { nav, opponents, setDetailId, t }: any) =>
        it.opponentId &&
        (() => {
          const parent = opponents.find((o: any) => o.id === it.opponentId);
          if (!parent) return null;
          return (
            <div className="detail-card">
              <h4>
                {t("list.section.linked_opponent", {
                  fallback: "Linked Opponent",
                })}
              </h4>
              <div
                onClick={() => {
                  setDetailId(null);
                  nav("op", parent.id);
                }}
                style={{
                  padding: "5px 6px",
                  cursor: "pointer",
                  fontSize: FS.s11,
                  color: "var(--tp)",
                  fontWeight: 500,
                }}
              >
                ← {pname(parent)}
                {parent.vat && (
                  <span
                    style={{
                      color: "var(--tm)",
                      fontWeight: 400,
                      marginLeft: 6,
                      fontSize: FS.s10,
                    }}
                  >
                    {t("field.vat", { fallback: "VAT" })} {parent.vat}
                  </span>
                )}
              </div>
            </div>
          );
        })(),
    },
    me: {
      toolbar: { search: searchOf("me") }, // 195 §C.3a — the list's own box
      items: mediators,
      et: "me",
      am: "me",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          { k: "specialty", l: "Specialty" },
          {
            k: "firm",
            l: "Practice",
            r: (i: any) => (
              <span style={{ fontWeight: 500, color: "var(--tp)" }}>
                {i.firm || "—"}
              </span>
            ),
          },
          { k: "email", l: "Email" },
        ],
        d: [
          { k: "specialty", l: "Specialty" },
          { k: "firm", l: "Practice" },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "barAssociation", l: "Professional Registration" },
          { k: "_addr", l: "Address", r: faddr },
          { k: "notes", l: "Notes" },
        ],
      },
      label: ENTITY_NOUNS.me,
      onDelete: (it: any, { del, mediators, setMediators }: any) =>
        del(SK.me, mediators, setMediators, it.id),
    },
    ar: {
      toolbar: { search: searchOf("ar") }, // 195 §C.3a — the list's own box
      items: arbiters,
      et: "ar",
      am: "ar",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          { k: "specialty", l: "Specialty" },
          {
            k: "firm",
            l: "Chamber",
            r: (i: any) => (
              <span style={{ fontWeight: 500, color: "var(--tp)" }}>
                {i.firm || "—"}
              </span>
            ),
          },
          { k: "email", l: "Email" },
        ],
        d: [
          { k: "specialty", l: "Specialty" },
          { k: "firm", l: "Chamber" },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "barAssociation", l: "Professional Registration" },
          { k: "_addr", l: "Address", r: faddr },
          { k: "notes", l: "Notes" },
        ],
      },
      label: ENTITY_NOUNS.ar,
      onDelete: (it: any, { arbiters, del, setArbiters }: any) =>
        del(SK.ar, arbiters, setArbiters, it.id),
    },
    op2: {
      toolbar: { search: searchOf("op2") }, // 195 §C.3a — the list's own box
      items: additionalOpponents,
      et: "op2",
      am: "op2",
      cols: {
        t: [
          {
            k: "_name",
            l: "Name",
            r: (i: any) => <span className="name-cell">{pname(i)}</span>,
          },
          { k: "specialty", l: "Specialty" },
          {
            k: "firm",
            l: "Organisation",
            r: (i: any) => (
              <span style={{ fontWeight: 500, color: "var(--tp)" }}>
                {i.firm || "—"}
              </span>
            ),
          },
          { k: "email", l: "Email" },
        ],
        d: [
          { k: "specialty", l: "Specialty" },
          { k: "firm", l: "Organisation" },
          { k: "email", l: "Email" },
          { k: "phone", l: "Phone", mo: 1 },
          { k: "barAssociation", l: "Professional Registration" },
          { k: "_addr", l: "Address", r: faddr },
          { k: "notes", l: "Notes" },
        ],
      },
      label: ENTITY_NOUNS.op2,
      onDelete: (it: any, { additionalOpponents, del, setAdditionalOpponents }: any) =>
        del(SK.op2, additionalOpponents, setAdditionalOpponents, it.id),
    },
};
};
